import { ventures, workspace } from './api';
import { logger } from '../helpers/logger';
import { amplitudeModule } from './amplitude';

export interface Banner {
  id: number;
  name: string;
  filePath: string;
  url: string;
  link?: string;
  isMobile: boolean;
}

export interface Product {
  id: number;
  productType: string;
  campaignId: number;
  banner: Banner;
}

export interface Campaign {
  id: number;
  name: string;
  status: string;
  active: boolean;
  products: Product[];
}

export interface BannerData {
  mobile: string;
  desktop: string;
  alt: string;
  idBanner: number;
  link?: string;
}

export interface PrescriptionParams {
  prescriptionId: number;
  doctorId: number;
}

export interface CampaignFilters {
  partner: string[];
  speciality?: string[];
  age?: string;
  prescriptionParams?: PrescriptionParams;
}

export const fetchActiveCampaigns = async (filters: CampaignFilters): Promise<Campaign[]> => {
  try {
    const response = await ventures.post<Campaign[]>('/campaigns/find-by-product/banner', filters);
    return response.data;
  } catch (error) {
    logger.error('Erro ao buscar campanhas:', error);
    throw error;
  }
};

export const processActiveBanners = (campaigns: Campaign[]): BannerData[] => {
  const bannerData: BannerData[] = [];

  campaigns.forEach((campaign) => {
    if (campaign.active) {
      let mobileBanner: Banner | null = null;
      let desktopBanner: Banner | null = null;
      let bannerLink: string | undefined;

      campaign.products.forEach((product) => {
        if (product.productType === 'banner' && product.banner) {
          if (product.banner.isMobile) {
            mobileBanner = product.banner;
          } else {
            desktopBanner = product.banner;
          }

          if (product.banner.link) {
            bannerLink = product.banner.link;
          }
        }
      });

      if (mobileBanner || desktopBanner) {
        bannerData.push({
          mobile: mobileBanner ? (mobileBanner as Banner).url : '',
          desktop: desktopBanner ? (desktopBanner as Banner).url : '',
          alt: desktopBanner ? (desktopBanner as Banner).name : mobileBanner ? (mobileBanner as Banner).name : 'Banner',
          link: bannerLink,
          idBanner: campaign.id,
        });
      }
    }
  });

  return bannerData;
};

const BANNER_CAMPAIGN_FLAG_PREFIX = 'banner_campaign_';

export const filterBannersByExperiment = (banners: BannerData[]): BannerData[] => {
  if (!amplitudeModule.isExperimentInitialized()) return banners;

  try {
    const variants = amplitudeModule.getAllVariants();
    const gatedBanners = new Map<number, boolean>();

    for (const flagKey of Object.keys(variants)) {
      if (!flagKey.startsWith(BANNER_CAMPAIGN_FLAG_PREFIX)) continue;

      const campaignIdSuffix = flagKey.slice(BANNER_CAMPAIGN_FLAG_PREFIX.length);
      if (campaignIdSuffix?.length === 0) continue;

      const campaignId = Number(campaignIdSuffix);
      if (!Number.isInteger(campaignId)) continue;

      const variant = variants[flagKey];
      const variantValue = variant?.value ?? variant?.key;
      gatedBanners.set(campaignId, variantValue === 'on');
    }

    if (gatedBanners.size === 0) return banners;

    return banners.filter((banner) => {
      if (!gatedBanners.has(banner.idBanner)) return true;
      return gatedBanners.get(banner.idBanner) === true;
    });
  } catch (error) {
    logger.error('Erro ao filtrar banners por experimento:', error);
    return banners;
  }
};

export const fetchActiveBanners = async (filters: CampaignFilters): Promise<BannerData[]> => {
  try {
    const campaigns = await fetchActiveCampaigns(filters);
    if (!campaigns || campaigns?.length === 0) return [];

    await amplitudeModule.whenExperimentsReady();
    return filterBannersByExperiment(processActiveBanners(campaigns));
  } catch (error) {
    logger.error('Erro ao buscar banners ativos:', error);
    return [];
  }
};

export const fetchDoctorRqes = async (doctorId: number): Promise<any> => {
  try {
    if (!doctorId) return [];

    const response = await workspace.get(`/medico/${doctorId}/rqes`);
    return response.data;
  } catch (error) {
    logger.error('Erro ao buscar RQEs do médico:', error);
    return [];
  }
};
